import React, { useState, useRef } from 'react';
import { ArrowLeft, Users, QrCode, Bell, MessageSquare, BarChart3, Shield, Layout, Calendar, FileText, TrendingUp, CheckCircle, Award, Zap, Building2, Phone, Mail, ExternalLink, Play, Image as ImageIcon, ChevronRight, DollarSign, Clock, Target, BookOpen, ClipboardCheck, CreditCard, UserCheck, Code, Database, Settings, Layers, Box, Lock, Video, Pause } from 'lucide-react';

export default function KastlerSalesPage({ darkMode, language, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const videoRefs = {
    admin: useRef(null),
    prof: useRef(null),
    etudiant: useRef(null),
    parent: useRef(null),
    demo: useRef(null)
  };
  
  const handleVideoPlay = (videoKey) => {
    // Arrêter toutes les autres vidéos
    Object.keys(videoRefs).forEach(key => {
      if (key !== videoKey && videoRefs[key].current) {
        videoRefs[key].current.pause();
        videoRefs[key].current.currentTime = 0;
      }
    });
    
    // Lancer la vidéo sélectionnée
    if (videoRefs[videoKey].current) {
      videoRefs[videoKey].current.play();
      setPlayingVideo(videoKey);
    }
  };
  
  const handleVideoPause = (videoKey) => {
    if (videoRefs[videoKey].current) {
      videoRefs[videoKey].current.pause();
      if (playingVideo === videoKey) {
        setPlayingVideo(null);
      }
    }
  };

  const content = {
    fr: {
      title: 'Plateforme de Gestion Scolaire Alfred Kastler',
      subtitle: 'Solution Complète & Éprouvée pour Tous Types d\'Établissements Éducatifs',
      backButton: 'Retour',
      
      tabOverview: 'Présentation',
      tabSchool: 'École Kastler',
      tabDevelopment: 'Développement',
      tabArchitecture: 'Architecture',
      tabFeatures: 'Fonctionnalités',
      tabRoles: 'Rôles Utilisateurs',
      tabGallery: 'Galerie',
      tabResults: 'Résultats',
      
      heroTitle: 'Transformez la Gestion de Votre École',
      heroSubtitle: 'Solution tout-en-un testée et approuvée en production réelle avec plus de 400 étudiants et 30 professeurs à l\'école Alfred Kastler',
      heroStats: [
        { value: '400+', label: 'Étudiants Gérés' },
        { value: '30+', label: 'Professeurs Actifs' },
        { value: '85%', label: 'Gain de Temps' },
        { value: '100%', label: 'Satisfaction' }
      ],
      
      overviewTitle: 'Une Solution Éprouvée en Production Réelle',
      overviewIntro: 'Notre plateforme est actuellement utilisée avec succès à l\'école Alfred Kastler depuis plusieurs mois. Ce n\'est pas un prototype - c\'est un système complet, testé et validé par des centaines d\'utilisateurs quotidiens.',
      
      provenTitle: ' images/abdo.png Système Validé en Conditions Réelles',
      provenPoints: [
        'Déjà déployé et fonctionnel à l\'école Alfred Kastler',
        'Plus de 400 étudiants l\'utilisent quotidiennement',
        '30+ professeurs gèrent leurs classes avec la plateforme',
        'Système stable avec 99.9% de disponibilité',
        'Feedback positif de tous les utilisateurs',
        'Améliorations continues basées sur l\'utilisation réelle'
      ],
      
      whyChooseTitle: 'Pourquoi Choisir Notre Solution ?',
      whyChoosePoints: [
        {
          icon: CheckCircle,
          title: 'Solution Prête à l\'Emploi',
          desc: 'Pas de développement à partir de zéro - déployez une solution qui fonctionne déjà'
        },
        {
          icon: Shield,
          title: 'Testée et Fiable',
          desc: 'Validée par des centaines d\'utilisateurs en conditions réelles'
        },
        {
          icon: TrendingUp,
          title: 'ROI Rapide',
          desc: 'Économisez 85% du temps administratif dès le premier mois'
        },
        {
          icon: Target,
          title: 'Support Complet',
          desc: 'Formation, installation et support technique inclus'
        }
      ],
      
      targetSchools: 'Pour Tous Types d\'Établissements',
      targetSchoolsList: [
        { icon: '', name: 'Crèches & Garderies', desc: 'Gestion des tout-petits, suivi développement' },
        { icon: '', name: 'Préscolaire', desc: 'Activités, progression, communication parents' },
        { icon: '', name: 'École Primaire', desc: 'Notes, devoirs, absences, bulletins' },
        { icon: '', name: 'Collège', desc: 'Gestion académique complète, examens' },
        { icon: '', name: 'Lycée', desc: 'Orientation, examens nationaux, dossiers' },
        { icon: '', name: 'Enseignement Privé', desc: 'Facturation, comptabilité, marketing' }
      ],
      
      aboutKastler: 'À Propos de l\'École Alfred Kastler',
      kastlerDescription: 'L\'école Alfred Kastler est un établissement d\'enseignement privé reconnu basé à Casablanca. Fondée pour offrir une éducation de qualité du préscolaire au lycée, l\'école accueille plus de 400 élèves et emploie une équipe pédagogique de plus de 30 professeurs qualifiés.',
      
      kastlerMission: 'Mission',
      kastlerMissionText: 'Offrir une éducation d\'excellence qui prépare les élèves à devenir des citoyens responsables, créatifs et capables de relever les défis du 21ème siècle.',
      
      kastlerLevels: 'Niveaux d\'Enseignement',
      levels: [
        { name: 'Préscolaire', desc: 'Petite, moyenne et grande section' },
        { name: 'Primaire', desc: 'CP à CM2 - Cycle fondamental' },
        { name: 'Collège', desc: '6ème à 3ème - Préparation au brevet' },
        { name: 'Lycée', desc: '2nde à Terminale - Préparation au bac' }
      ],
      
      campusPhotos: 'Campus Alfred Kastler',
      visitWebsite: 'Visiter le site web',
      
      myRoleTitle: 'Développement & Réalisation',
      roleSubtitle: 'Projet Commercial - Solution Complète Développée',
      roleIntro: 'En tant que développeur Full Stack, j\'ai conçu et développé l\'intégralité de cette plateforme pour l\'école Alfred Kastler. Ce projet commercial a été réalisé de A à Z avec les responsabilités suivantes :',
      
      responsibilities: [
        'Analyse approfondie des besoins avec la direction de Kastler',
        'Conception de l\'architecture technique complète (Frontend, Backend, BDD)',
        'Développement de l\'interface utilisateur React.js responsive',
        'Création du système de routage multi-rôles (Admin, Prof, Parent, Élève)',
        'Développement Backend Node.js/Express avec API RESTful',
        'Conception et implémentation de la base de données MongoDB',
        'Système d\'authentification JWT sécurisé',
        'Développement du chat temps réel avec Socket.io',
        'Dashboard financier avec statistiques et rapports',
        'Module de génération de badges QR Code personnalisés',
        'Système de notifications WhatsApp automatiques',
        'Intégration portail parents avec suivi en temps réel',
        'Génération automatique de bulletins de notes PDF',
        'Gestion complète des absences et retards',
        'Module exercices et devoirs en ligne',
        'Tests, déploiement et formation des utilisateurs',
        'Développement du site web institutionnel kastler.ma'
      ],
      
      websiteTitle: 'Site Web Institutionnel',
      websiteDesc: 'En plus de la plateforme de gestion, j\'ai également développé le site web officiel de l\'école (kastler.ma) avec :',
      websiteFeatures: [
        'Design moderne et responsive',
        'Présentation complète de l\'école',
        'Section actualités et événements',
        'Formulaire de contact et préinscription',
        'Galerie photos et vidéos',
        'Optimisation SEO pour visibilité Google',
        'Interface multilingue (Français/Arabe)'
      ],
      
      techStackTitle: 'Stack Technique',
      frontend: 'Frontend',
      frontendTech: ['React.js 18', 'React Router', 'Context API', 'Tailwind CSS', 'Axios', 'Socket.io Client'],
      backend: 'Backend',
      backendTech: ['Node.js', 'Express.js', 'Socket.io', 'JWT', 'Bcrypt', 'Multer'],
      database: 'Base de données',
      databaseTech: ['MongoDB', 'Mongoose ODM', 'Redis Cache'],
      integrations: 'Intégrations',
      integrationsTech: ['WhatsApp Business API', 'PDF Generation', 'Email Service', 'QR Code Library'],
      
      architectureTitle: 'Architecture du Système',
      architectureDesc: 'Architecture client-serveur modulaire scalable avec communication temps réel et gestion centralisée des données.',
      
      layersTitle: 'Couches Applicatives',
      presentationLayer: 'Couche Présentation',
      presentationLayerDesc: 'Interface React.js responsive avec 4 interfaces différentes selon les rôles (Admin, Prof, Parent, Élève)',
      businessLayer: 'Couche Métier',
      businessLayerDesc: 'API REST Node.js/Express gérant la logique métier, validations et calculs complexes',
      dataLayer: 'Couche Données',
      dataLayerDesc: 'MongoDB avec 18+ collections optimisées et relations structurées',
      realtimeLayer: 'Communication Temps Réel',
      realtimeLayerDesc: 'Socket.io pour chat instantané, notifications push et mise à jour en temps réel',
      
      modulesTitle: 'Modules du Système',
      modulesSubtitle: '10 Modules Interconnectés',
      
      modules: [
        {
          name: 'Module Administration',
          description: 'Dashboard global avec vue d\'ensemble complète, gestion des utilisateurs (création, modification, suppression), gestion des filières et niveaux, création des emplois du temps, configuration système et paramètres, génération de rapports globaux'
        },
        {
          name: 'Module Badges QR Code',
          description: 'Génération automatique de cartes d\'étudiant avec QR code unique, scan rapide pour prise de présence (30 secondes/classe), contrôle d\'accès sécurisé, identification instantanée, impression de badges professionnels avec photo'
        },
        {
          name: 'Module Portail Parents',
          description: 'Interface dédiée parents pour suivi en temps réel, consultation notes et moyennes, suivi absences et retards, consultation emploi du temps, accès aux bulletins PDF, messagerie avec enseignants, notifications automatiques'
        },
        {
          name: 'Module WhatsApp',
          description: 'Envoi automatique de notifications WhatsApp aux parents, alertes absences et retards, communications événements, rappels paiements, messages personnalisés, taux de délivrance 98%'
        },
        {
          name: 'Module Pédagogique',
          description: 'Gestion complète des notes avec calculs automatiques, création et gestion des examens, génération automatique de bulletins PDF professionnels, suivi progression par élève, statistiques par classe et matière'
        },
        {
          name: 'Module Scolarité',
          description: 'Gestion absences (manuelle ou QR code), justificatifs numériques, alertes automatiques parents, suivi retards, statistiques détaillées, génération attestations et certificats'
        },
        {
          name: 'Module Exercices',
          description: 'Publication exercices et devoirs en ligne, soumission travaux par élèves, correction facilitée, feedback instantané, historique complet, zéro papier'
        },
        {
          name: 'Module Comptabilité',
          description: 'Suivi détaillé paiements et échéances, calcul automatique selon filières, génération reçus et factures, rapports financiers, alertes paiements en retard, statistiques recouvrement'
        },
        {
          name: 'Module Messagerie',
          description: 'Chat instantané entre tous acteurs (admin, profs, parents, élèves), historique conversations, notifications temps réel, pièces jointes, groupes de discussion'
        },
        {
          name: 'Module Enseignant',
          description: 'Consultation cours assignés, saisie notes et moyennes, gestion absences élèves, communication avec élèves, accès emplois du temps, ressources pédagogiques'
        }
      ],
      
      featuresTitle: 'Fonctionnalités Complètes',
      featuresSubtitle: 'Tout ce dont votre école a besoin dans une seule plateforme',
      
      featuresList: [
        {
          icon: QrCode,
          title: 'Badges QR Code Intelligents',
          description: 'Carte d\'étudiant digitale avec QR code unique pour chaque élève. Scan rapide pour enregistrer présence/absence, contrôle d\'accès sécurisé, et identification instantanée. Génération automatique de badges imprimables avec photo et informations.',
          benefits: ['Prise de présence en 30 secondes', 'Sécurité renforcée', 'Cartes digitales ou imprimables']
        },
        {
          icon: Layout,
          title: 'Portail Parents Complet',
          description: 'Interface dédiée permettant aux parents de suivre en temps réel la scolarité de leurs enfants. Consultation des notes, absences, emploi du temps, bulletins et communication directe avec les enseignants. Notifications automatiques.',
          benefits: ['Parents informés 24/7', 'Communication facilitée', 'Transparence totale']
        },
        {
          icon: MessageSquare,
          title: 'Intégration WhatsApp',
          description: 'Envoi automatique de notifications WhatsApp aux parents pour absences, retards, événements importants et communications urgentes. Système fiable avec confirmation de réception.',
          benefits: ['Communication instantanée', 'Taux d\'ouverture 98%', 'Réponse rapide']
        },
        {
          icon: BarChart3,
          title: 'Bulletins de Notes Numériques',
          description: 'Système complet de gestion des notes avec calcul automatique des moyennes, coefficients, et génération de bulletins professionnels. Export PDF, envoi par email, et archivage sécurisé.',
          benefits: ['Calculs automatiques', 'Bulletins professionnels', 'Zéro erreur']
        },
        {
          icon: FileText,
          title: 'Gestion des Absences & Retards',
          description: 'Suivi précis des présences avec enregistrement manuel ou par QR code. Justificatifs numériques, statistiques détaillées, et alertes automatiques aux parents en cas d\'absence non justifiée.',
          benefits: ['Suivi en temps réel', 'Alertes automatiques', 'Statistiques complètes']
        },
        {
          icon: BookOpen,
          title: 'Exercices & Devoirs en Ligne',
          description: 'Les professeurs peuvent publier exercices, devoirs et ressources pédagogiques. Les étudiants soumettent leurs travaux en ligne. Correction facilitée et feedback instantané.',
          benefits: ['Zéro papier', 'Correction simplifiée', 'Historique complet']
        },
        {
          icon: ClipboardCheck,
          title: 'Gestion Administrative',
          description: 'Dashboard complet pour l\'administration avec gestion des inscriptions, des classes, des emplois du temps, et génération de tous les documents officiels (attestations, certificats).',
          benefits: ['Tout centralisé', 'Rapports automatiques', 'Documents instantanés']
        },
        {
          icon: CreditCard,
          title: 'Suivi des Paiements',
          description: 'Module comptable avec suivi des frais de scolarité, échéanciers personnalisés, génération de reçus automatiques, et alertes pour paiements en retard.',
          benefits: ['Comptabilité claire', 'Reçus automatiques', 'Rappels intelligents']
        },
        {
          icon: MessageSquare,
          title: 'Messagerie Intégrée',
          description: 'Chat instantané entre tous les acteurs (administration, professeurs, parents, étudiants) avec historique complet et notifications en temps réel.',
          benefits: ['Communication fluide', 'Tout centralisé', 'Réponses rapides']
        },
        {
          icon: UserCheck,
          title: 'Système Multi-Rôles',
          description: 'Accès différencié selon les rôles : Admin, Prof, Parent, Étudiant. Chaque utilisateur voit uniquement ce qui le concerne avec permissions personnalisées.',
          benefits: ['Sécurité maximale', 'Interface adaptée', 'Droits granulaires']
        }
      ],
      
      benefitsTitle: 'Avantages Concrets',
      
      timeSection: 'Gain de Temps Massif',
      timePoints: [
        '85% de réduction du temps administratif',
        'Prise de présence en 30 secondes au lieu de 10 minutes',
        'Génération de bulletins automatique (0 erreur)',
        'Plus de paperasse - tout est numérique'
      ],
      
      communicationSection: 'Communication Améliorée',
      communicationPoints: [
        'Parents informés instantanément via WhatsApp',
        'Taux de satisfaction famille: 100%',
        'Réduction des malentendus et conflits',
        'Historique complet de toutes les communications'
      ],
      
      dataSection: 'Données Sécurisées & Organisées',
      dataPoints: [
        'Toutes les informations centralisées et sécurisées',
        'Sauvegarde automatique quotidienne',
        'Plus de perte de documents ou données',
        'Accès 24/7 depuis n\'importe où'
      ],
      
      financialSection: 'ROI Rapide',
      financialPoints: [
        'Amortissement en moins de 6 mois',
        'Réduction des coûts papier et impression',
        'Moins d\'erreurs administratives coûteuses',
        'Personnel plus productif'
      ],
      
      techTitle: 'Technologie Moderne & Robuste',
      techSubtitle: 'Architecture professionnelle pour performances et sécurité maximales',
      
      techStack: {
        frontend: {
          title: 'Interface Utilisateur',
          tech: ['React.js', 'Responsive Design', 'Progressive Web App'],
          desc: 'Interface moderne, rapide et intuitive fonctionnant sur tous les appareils'
        },
        backend: {
          title: 'Serveur & Logique',
          tech: ['Node.js', 'Express.js', 'API REST'],
          desc: 'Architecture robuste et scalable pour gérer des milliers d\'utilisateurs'
        },
        database: {
          title: 'Base de Données',
          tech: ['MongoDB', 'Redis Cache', 'Backups automatiques'],
          desc: 'Stockage sécurisé avec performances optimales et sauvegardes quotidiennes'
        },
        security: {
          title: 'Sécurité',
          tech: ['Cryptage SSL/TLS', 'Authentification JWT', 'Protection GDPR'],
          desc: 'Niveau de sécurité bancaire pour protéger les données sensibles'
        }
      },
      
      techFeatures: [
        'Application Web + PWA installable sur mobile',
        'Fonctionne sur smartphone, tablette et ordinateur',
        'Mode hors-ligne pour fonctions essentielles',
        'Mises à jour automatiques sans interruption',
        'Support technique et maintenance inclus'
      ],
      
      galleryTitle: 'Galerie & Démonstration',
      gallerySubtitle: 'Découvrez la plateforme en action sur tous les appareils',
      demoVideoTitle: 'Vidéo de Démonstration Complète',
      demoVideoDesc: 'Regardez comment la plateforme fonctionne en conditions réelles',
      screenshotsTitle: 'Captures d\'Écran',
      screenshotsDesc: 'Parcourez les différentes interfaces de la plateforme',
      
      resultsTitle: 'Impact & Résultats à Kastler',
      
      statsTitle: 'Chiffres Clés',
      stats: [
        { value: '400+', label: 'Étudiants Gérés' },
        { value: '30+', label: 'Professeurs Actifs' },
        { value: '85%', label: 'Gain de Temps' },
        { value: '100%', label: 'Taux d\'Adoption' }
      ],
      
      challengesMetTitle: 'Défis Techniques Relevés',
      challengesMet: [
        'Architecture scalable pour croissance future importante',
        'Interface intuitive pour 4 profils utilisateurs différents',
        'Système de permissions complexe avec sécurité maximale',
        'Optimisation des performances pour requêtes temps réel',
        'Synchronisation temps réel entre modules sans perte',
        'Disponibilité 24/7 avec gestion erreurs et récupération auto'
      ],
      
      concreteResultsTitle: 'Résultats Concrets Mesurables',
      concreteResults: [
        'Réduction de 85% du temps de traitement administratif',
        'Élimination complète des erreurs de paiement',
        'Communication école-famille améliorée (satisfaction 100%)',
        'Génération instantanée de rapports (vs heures avant)',
        'Adoption complète du personnel en 2 semaines',
        'ROI positif dès les 6 premiers mois'
      ],
      
      testimonialTitle: 'Témoignage École Alfred Kastler',
      testimonialQuote: 'Cette plateforme a complètement transformé notre façon de gérer l\'école. L\'administration est 10 fois plus rapide, les parents sont ravis de pouvoir suivre leurs enfants en temps réel, et les professeurs gagnent un temps précieux. C\'est un investissement qui s\'est rentabilisé en quelques mois.',
      testimonialAuthor: 'Direction Alfred Kastler',
      testimonialSchool: 'École Alfred Kastler - Casablanca',
      
      realUseTitle: 'Utilisation Quotidienne Réelle',
      realUsePoints: [
        '400+ étudiants utilisent la plateforme chaque jour',
        '30 professeurs gèrent leurs classes via le système',
        'Prise de présence QR Code en 30 secondes par classe',
        'Parents reçoivent notifications WhatsApp en temps réel',
        'Bulletins générés et envoyés automatiquement',
        'Zéro interruption de service depuis le déploiement'
      ],
      
      ctaTitle: 'Prêt à Moderniser Votre École ?',
      ctaSubtitle: 'Rejoignez les établissements qui ont déjà fait le choix de l\'efficacité',
      ctaButton: 'Demander une Démo Personnalisée',
      ctaSecondButton: 'Obtenir un Devis',
      
      ctaFeatures: [
        'Démo gratuite adaptée à votre établissement',
        'Installation et formation complètes incluses',
        'Support technique dédié',
        'Personnalisation selon vos besoins spécifiques',
        'Garantie satisfaction',
        'Maintenance et mises à jour incluses'
      ],
      
      contactTitle: 'Contactez-nous',
      contactEmail: 'contact@abdelmajid-erraki.com',
      contactPhone: '+212 6XX XXX XXX',
      
      projectInfoTitle: 'Informations Projet',
      projectType: 'Projet Commercial - Solution Vendue',
      projectDuration: 'Durée: 5 mois de développement',
      projectClient: 'Client: École Alfred Kastler',
      projectStatus: 'Statut: En production depuis 6+ mois'
    },
    ar: {
      title: 'منصة إدارة مدرسية Alfred Kastler',
      subtitle: 'حل كامل ومجرب لجميع أنواع المؤسسات التعليمية',
      backButton: 'رجوع',
      
      tabOverview: 'نظرة عامة',
      tabSchool: 'مدرسة Kastler',
      tabDevelopment: 'التطوير',
      tabArchitecture: 'الهندسة',
      tabFeatures: 'المميزات',
      tabRoles: 'أدوار المستخدمين',
      tabGallery: 'المعرض',
      tabResults: 'النتائج',
      
      heroTitle: 'حوّل إدارة مدرستك',
      heroSubtitle: 'حل شامل مجرب ومعتمد مع أكثر من 400 طالب و30 أستاذ في مدرسة Alfred Kastler',
      heroStats: [
        { value: '400+', label: 'طالب' },
        { value: '30+', label: 'أستاذ' },
        { value: '85%', label: 'توفير الوقت' },
        { value: '100%', label: 'رضا' }
      ],
      
      overviewTitle: 'حل مجرب في الإنتاج الفعلي',
      overviewIntro: 'منصتنا مستخدمة بنجاح في مدرسة Alfred Kastler منذ عدة أشهر.',
      
      provenTitle: 'images/abdo.png نظام معتمد',
      provenPoints: [
        'منتشر في مدرسة Alfred Kastler',
        'أكثر من 400 طالب',
        '30+ أستاذ',
        'توفر 99.9%',
        'ردود فعل إيجابية',
        'تحسينات مستمرة'
      ],
      
      whyChooseTitle: 'لماذا تختار حلنا؟',
      whyChoosePoints: [
        { icon: CheckCircle, title: 'حل جاهز', desc: 'لا تطوير من الصفر' },
        { icon: Shield, title: 'مجرب', desc: 'معتمد من مئات المستخدمين' },
        { icon: TrendingUp, title: 'عائد سريع', desc: 'وفر 85% من الوقت' },
        { icon: Target, title: 'دعم كامل', desc: 'تدريب وتثبيت' }
      ],
      
      targetSchools: 'لجميع أنواع المؤسسات',
      targetSchoolsList: [
        { icon: '', name: 'حضانات', desc: 'إدارة الصغار' },
        { icon: '', name: 'ما قبل المدرسة', desc: 'أنشطة وتطور' },
        { icon: '', name: 'الابتدائي', desc: 'نقط وواجبات' },
        { icon: '', name: 'الإعدادي', desc: 'إدارة أكاديمية' },
        { icon: '', name: 'الثانوي', desc: 'توجيه وامتحانات' },
        { icon: '', name: 'التعليم الخاص', desc: 'فوترة ومحاسبة' }
      ],
      
      aboutKastler: 'عن مدرسة Alfred Kastler',
      kastlerDescription: 'مدرسة Alfred Kastler مؤسسة تعليم خاص معترف بها في الدار البيضاء.',
      
      kastlerMission: 'المهمة',
      kastlerMissionText: 'تقديم تعليم متميز',
      
      kastlerLevels: 'مستويات التعليم',
      levels: [
        { name: 'ما قبل المدرسة', desc: 'أقسام صغيرة ومتوسطة' },
        { name: 'الابتدائي', desc: 'الدورة الأساسية' },
        { name: 'الإعدادي', desc: 'إعداد الشهادة' },
        { name: 'الثانوي', desc: 'إعداد البكالوريا' }
      ],
      
      campusPhotos: 'حرم Alfred Kastler',
      visitWebsite: 'زيارة الموقع',
      
      myRoleTitle: 'التطوير والإنجاز',
      roleSubtitle: 'مشروع تجاري',
      roleIntro: 'كمطور Full Stack، صممت وطورت هذه المنصة بالكامل.',
      
      responsibilities: [
        'تحليل الاحتياجات',
        'تصميم الهندسة',
        'تطوير الواجهة React.js',
        'نظام متعدد الأدوار',
        'Backend Node.js/Express',
        'قاعدة MongoDB',
        'مصادقة JWT',
        'دردشة Socket.io',
        'لوحة مالية',
        'بطاقات QR',
        'إشعارات واتساب',
        'بوابة الأولياء',
        'بيانات PDF',
        'إدارة الغياب',
        'تمارين أونلاين',
        'اختبار ونشر',
        'تطوير موقع kastler.ma'
      ],
      
      websiteTitle: 'الموقع الرسمي',
      websiteDesc: 'بالإضافة للمنصة، طورت موقع المدرسة الرسمي:',
      websiteFeatures: [
        'تصميم حديث ومتجاوب',
        'عرض كامل للمدرسة',
        'أخبار وفعاليات',
        'نموذج اتصال',
        'معرض صور',
        'تحسين SEO',
        'متعدد اللغات'
      ],
      
      techStackTitle: 'التقنيات',
      frontend: 'الواجهة',
      frontendTech: ['React.js 18', 'React Router', 'Context API', 'Tailwind CSS', 'Axios', 'Socket.io Client'],
      backend: 'الخادم',
      backendTech: ['Node.js', 'Express.js', 'Socket.io', 'JWT', 'Bcrypt', 'Multer'],
      database: 'قاعدة البيانات',
      databaseTech: ['MongoDB', 'Mongoose ODM', 'Redis Cache'],
      integrations: 'التكاملات',
      integrationsTech: ['WhatsApp API', 'PDF Generation', 'Email', 'QR Code'],
      
      architectureTitle: 'هندسة النظام',
      architectureDesc: 'بنية معيارية قابلة للتوسع.',
      
      layersTitle: 'الطبقات',
      presentationLayer: 'طبقة العرض',
      presentationLayerDesc: 'واجهة React.js متجاوبة مع 4 واجهات',
      businessLayer: 'طبقة الأعمال',
      businessLayerDesc: 'API REST Node.js/Express',
      dataLayer: 'طبقة البيانات',
      dataLayerDesc: 'MongoDB مع 18+ مجموعة',
      realtimeLayer: 'الاتصال الفوري',
      realtimeLayerDesc: 'Socket.io للدردشة والإشعارات',
      
      modulesTitle: 'وحدات النظام',
      modulesSubtitle: '10 وحدات مترابطة',
      
      modules: [
        { name: 'وحدة الإدارة', description: 'لوحة تحكم شاملة وإدارة المستخدمين والتقارير' },
        { name: 'بطاقات QR', description: 'توليد بطاقات طلاب مع رموز QR فريدة' },
        { name: 'بوابة الأولياء', description: 'واجهة مخصصة للمتابعة الفورية' },
        { name: 'وحدة واتساب', description: 'إشعارات تلقائية عبر واتساب' },
        { name: 'وحدة التربوية', description: 'إدارة النقط والبيانات' },
        { name: 'وحدة الدراسة', description: 'إدارة الغياب والتأخر' },
        { name: 'وحدة التمارين', description: 'نشر تمارين وواجبات أونلاين' },
        { name: 'وحدة المحاسبة', description: 'متابعة المدفوعات والفواتير' },
        { name: 'وحدة المراسلة', description: 'دردشة فورية بين الجميع' },
        { name: 'وحدة المعلم', description: 'إدارة الدروس والتواصل' }
      ],
      
      featuresTitle: 'المميزات الكاملة',
      featuresSubtitle: 'كل ما تحتاجه مدرستك',
      
      featuresList: [
        { icon: QrCode, title: 'بطاقات QR ذكية', description: 'بطاقة طالب رقمية مع QR فريد', benefits: ['حضور في 30 ثانية', 'أمان معزز', 'رقمي أو مطبوع'] },
        { icon: Layout, title: 'بوابة الأولياء', description: 'واجهة لمتابعة الأبناء', benefits: ['مطلعون 24/7', 'تواصل سهل', 'شفافية'] },
        { icon: MessageSquare, title: 'تكامل واتساب', description: 'إشعارات تلقائية', benefits: ['فوري', 'معدل فتح 98%', 'سريع'] },
        { icon: BarChart3, title: 'بيانات رقمية', description: 'نظام نقط كامل', benefits: ['حسابات تلقائية', 'احترافي', 'صفر خطأ'] },
        { icon: FileText, title: 'إدارة الغياب', description: 'متابعة دقيقة', benefits: ['فوري', 'تنبيهات', 'إحصائيات'] },
        { icon: BookOpen, title: 'تمارين أونلاين', description: 'نشر التمارين', benefits: ['لا ورق', 'تصحيح سهل', 'سجل'] },
        { icon: ClipboardCheck, title: 'إدارة إدارية', description: 'لوحة تحكم شاملة', benefits: ['مركزي', 'تقارير', 'وثائق'] },
        { icon: CreditCard, title: 'متابعة المدفوعات', description: 'وحدة محاسبية', benefits: ['واضح', 'إيصالات', 'تذكيرات'] },
        { icon: MessageSquare, title: 'مراسلة', description: 'دردشة فورية', benefits: ['سلس', 'مركزي', 'سريع'] },
        { icon: UserCheck, title: 'متعدد الأدوار', description: 'وصول حسب الدور', benefits: ['أمان', 'مكيف', 'دقيق'] }
      ],
      
      benefitsTitle: 'فوائد ملموسة',
      
      timeSection: 'توفير هائل للوقت',
      timePoints: ['تقليل 85%', 'حضور 30 ثانية', 'بيانات تلقائية', 'لا ورق'],
      
      communicationSection: 'تواصل محسّن',
      communicationPoints: ['آباء مطلعون فوراً', 'رضا 100%', 'تقليل سوء الفهم', 'سجل كامل'],
      
      dataSection: 'بيانات آمنة',
      dataPoints: ['مركزي وآمن', 'نسخ احتياطي', 'لا ضياع', 'وصول 24/7'],
      
      financialSection: 'عائد سريع',
      financialPoints: ['استرداد في 6 أشهر', 'تقليل التكاليف', 'أخطاء أقل', 'إنتاجية أعلى'],
      
      techTitle: 'تكنولوجيا حديثة',
      techSubtitle: 'بنية احترافية',
      
      techStack: {
        frontend: { title: 'الواجهة', tech: ['React.js', 'Responsive', 'PWA'], desc: 'واجهة حديثة' },
        backend: { title: 'الخادم', tech: ['Node.js', 'Express.js', 'API REST'], desc: 'بنية قوية' },
        database: { title: 'قاعدة البيانات', tech: ['MongoDB', 'Redis', 'Backups'], desc: 'تخزين آمن' },
        security: { title: 'الأمان', tech: ['SSL/TLS', 'JWT', 'GDPR'], desc: 'أمان مصرفي' }
      },
      
      techFeatures: ['ويب + PWA', 'كل الأجهزة', 'وضع غير متصل', 'تحديثات تلقائية', 'دعم تقني'],
      
      galleryTitle: 'المعرض والتجربة',
      gallerySubtitle: 'اكتشف المنصة تعمل على جميع الأجهزة',
      demoVideoTitle: 'فيديو توضيحي كامل',
      demoVideoDesc: 'شاهد كيف تعمل المنصة',
      screenshotsTitle: 'لقطات الشاشة',
      screenshotsDesc: 'تصفح الواجهات المختلفة',
      
      resultsTitle: 'التأثير والنتائج',
      
      statsTitle: 'أرقام رئيسية',
      stats: [
        { value: '400+', label: 'طالب' },
        { value: '30+', label: 'أستاذ' },
        { value: '85%', label: 'توفير' },
        { value: '100%', label: 'تبني' }
      ],
      
      challengesMetTitle: 'تحديات تقنية',
      challengesMet: ['بنية قابلة للتوسع', 'واجهة بديهية', 'أذونات معقدة', 'تحسين الأداء', 'مزامنة فورية', 'توفر 24/7'],
      
      concreteResultsTitle: 'نتائج ملموسة',
      concreteResults: ['تقليل 85%', 'صفر أخطاء دفع', 'تواصل محسّن', 'تقارير فورية', 'تبني في أسبوعين', 'عائد إيجابي'],
      
      testimonialTitle: 'شهادة Kastler',
      testimonialQuote: 'هذه المنصة غيرت طريقة إدارتنا.',
      testimonialAuthor: 'إدارة Alfred Kastler',
      testimonialSchool: 'مدرسة Alfred Kastler - الدار البيضاء',
      
      realUseTitle: 'استخدام يومي',
      realUsePoints: ['400+ طالب', '30 أستاذ', 'حضور 30 ثانية', 'إشعارات فورية', 'بيانات تلقائية', 'صفر انقطاع'],
      
      ctaTitle: 'جاهز لتحديث مدرستك؟',
      ctaSubtitle: 'انضم للمؤسسات الناجحة',
      ctaButton: 'طلب عرض',
      ctaSecondButton: 'عرض أسعار',
      
      ctaFeatures: ['عرض مجاني', 'تثبيت وتدريب', 'دعم تقني', 'تخصيص', 'ضمان رضا', 'صيانة مضمنة'],
      
      contactTitle: 'اتصل بنا',
      contactEmail: 'contact@abdelmajid-erraki.com',
      contactPhone: '+212 6XX XXX XXX',
      
      projectInfoTitle: 'معلومات المشروع',
      projectType: 'مشروع تجاري',
      projectDuration: 'المدة: 5 أشهر',
      projectClient: 'العميل: مدرسة Alfred Kastler',
      projectStatus: 'الحالة: في الإنتاج منذ 6+ أشهر'
    }
  };

  const t = content[language];
  const isRTL = language === 'ar';

  const tabs = [
    { id: 'overview', label: t.tabOverview },
    { id: 'school', label: t.tabSchool },
    { id: 'development', label: t.tabDevelopment },
    { id: 'architecture', label: t.tabArchitecture },
    { id: 'features', label: t.tabFeatures },
    { id: 'roles', label: t.tabRoles },
    { id: 'gallery', label: t.tabGallery },
    { id: 'results', label: t.tabResults }
  ];

  const userRoles = [
    {
      key: 'admin',
      role: 'Administrateur',
      icon: Users,
      color: 'from-blue-500 to-indigo-600',
      features: [
        'Dashboard global avec statistiques complètes',
        'Gestion complète des utilisateurs (création/modification/suppression)',
        'Configuration du système et paramètres',
        'Génération de rapports détaillés',
        'Gestion des filières et niveaux scolaires',
        'Validation des inscriptions',
        'Supervision de tous les modules'
      ],
      access: 'Accès complet à toutes les fonctionnalités et données',
      videoFile: 'admin.mp4'
    },
    {
      key: 'prof',
      role: 'Professeur',
      icon: BookOpen,
      color: 'from-purple-500 to-pink-600',
      features: [
        'Saisie des notes et calcul des moyennes',
        'Gestion des absences de ses classes',
        'Publication d\'exercices et devoirs en ligne',
        'Communication avec élèves et parents',
        'Consultation de l\'emploi du temps',
        'Accès aux ressources pédagogiques',
        'Correction des travaux en ligne'
      ],
      access: 'Fonctionnalités pédagogiques complètes pour ses classes',
      videoFile: 'prof.mp4'
    },
    {
      key: 'etudiant',
      role: 'Étudiant',
      icon: Award,
      color: 'from-cyan-500 to-blue-600',
      features: [
        'Consultation des notes personnelles',
        'Emploi du temps personnalisé',
        'Soumission des devoirs en ligne',
        'Messagerie avec professeurs',
        'Accès aux ressources éducatives',
        'Suivi des absences personnelles',
        'Consultation des exercices'
      ],
      access: 'Outils d\'apprentissage et suivi personnel',
      videoFile: 'etudiant.mp4'
    },
    {
      key: 'parent',
      role: 'Parent',
      icon: UserCheck,
      color: 'from-orange-500 to-red-600',
      features: [
        'Suivi des notes de ses enfants en temps réel',
        'Consultation des absences et retards',
        'Accès aux bulletins PDF',
        'Communication directe avec les enseignants',
        'Notifications automatiques via WhatsApp',
        'Paiements en ligne sécurisés',
        'Emploi du temps des enfants'
      ],
      access: 'Suivi scolaire complet et communication avec l\'école',
      videoFile: 'parent.mp4'
    }
  ];

  const galleryContent = [
    {
      category: 'Vidéo de Démonstration Complète',
      items: [
        {
          title: 'Démo Complète Plateforme',
          description: 'Vidéo complète montrant toutes les fonctionnalités de la plateforme',
          type: 'video',
          image: null,
          videoFile: 'demo.mp4',
          details: [
            'Interface administrateur complète',
            'Génération et scan badges QR code',
            'Navigation portail parents',
            'Utilisation sur mobile et tablette',
            'Processus complet de gestion'
          ]
        }
      ]
    },
    {
      category: 'Badges QR Code - Recto/Verso',
      items: [
        {
          title: 'Carte Étudiant - Recto',
          description: 'Face avant professionnelle avec photo et informations',
          type: 'image',
          image: 'Carte_recto.png',
          details: [
            'Photo haute résolution',
            'Nom complet',
            'Niveau/filière',
            'Logo école',
            'Design professionnel'
          ]
        },
        {
          title: 'Carte Étudiant - Verso',
          description: 'Face arrière avec QR code unique',
          type: 'image',
          image: 'carte-etudiant-verso-pvc.png',
          details: [
            'QR code crypté unique',
            'Contact d\'urgence',
            'Règles de conduite',
            'Numéro étudiant',
            'Date validité'
          ]
        },
        {
          title: 'Page QR Code Étudiant',
          description: 'Interface de génération et affichage des QR codes',
          type: 'image',
          image: 'etudiant-site.png',
          details: [
            'Génération QR code instantanée',
            'Affichage informations étudiant',
            'Option impression',
            'Historique des scans',
            'Statistiques présence'
          ]
        }
      ]
    },
    {
      category: 'Interface Mobile - Application Responsive',
      items: [
        {
          title: 'Dashboard Mobile',
          description: 'Interface administrateur optimisée mobile',
          type: 'image',
          image: 'photo-dashboard-telephone.jpeg',
          details: [
            'Statistiques rapides',
            'Notifications push',
            'Gestion utilisateurs mobile',
            'Rapports optimisés mobile'
          ]
        },
        {
          title: 'Login Mobile',
          description: 'Page de connexion responsive sur mobile',
          type: 'image',
          image: 'photo-login-espace-telephone.jpeg',
          details: [
            'Connexion sécurisée mobile',
            'Interface intuitive',
            'Récupération mot de passe',
            'Biométrie supportée'
          ]
        }
      ]
    },
  
    
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'overview':
        return (
          <div className="space-y-6 md:space-y-8">
            {/* Hero Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {t.heroStats.map((stat, i) => (
                <div key={i} className={`text-center p-6 rounded-xl ${darkMode ? 'bg-gradient-to-br from-blue-900/40 to-indigo-900/40' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} border-2 border-blue-500`}>
                  <div className="text-4xl font-bold text-blue-500 mb-2">{stat.value}</div>
                  <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Main Intro */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-3xl font-bold mb-4 text-blue-500">{t.overviewTitle}</h2>
              <p className={`text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.overviewIntro}
              </p>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500 mb-6`}>
                <h3 className="text-2xl font-bold mb-4 text-blue-600">{t.provenTitle}</h3>
                <div className="space-y-3">
                  {t.provenPoints.map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Why Choose */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.whyChooseTitle}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {t.whyChoosePoints.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className={`p-5 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} hover:scale-105 transition-transform`}>
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600">
                            <Icon size={24} color="white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg mb-2 text-blue-500">{item.title}</h4>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Target Schools */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-6 text-center text-blue-600">{t.targetSchools}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {t.targetSchoolsList.map((school, i) => (
                  <div key={i} className={`p-5 rounded-xl text-center ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} border ${darkMode ? 'border-blue-800' : 'border-blue-200'} hover:scale-105 transition-transform`}>
                    <div className="text-4xl mb-3">{school.icon}</div>
                    <h4 className="font-bold text-lg mb-2 text-blue-600">{school.name}</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{school.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Info */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.projectInfoTitle}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Target size={24} className="text-blue-600" />
                    <p className="font-bold text-lg">{t.projectType}</p>
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar size={24} className="text-blue-600" />
                    <p className="font-bold text-lg">{t.projectDuration}</p>
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 size={24} className="text-blue-600" />
                    <p className="font-bold text-lg">{t.projectClient}</p>
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle size={24} className="text-blue-600" />
                    <p className="font-bold text-lg">{t.projectStatus}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'school':
        return (
          <div className="space-y-8">
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4 mb-6">
                <Building2 size={48} className="text-blue-600" />
                <div>
                  <h2 className="text-4xl font-bold text-blue-600">Alfred Kastler</h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    École Privée - Casablanca, Maroc
                  </p>
                </div>
              </div>
              <p className={`text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.kastlerDescription}
              </p>
              <a 
                href="https://kastler.ma" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
              >
                <ExternalLink size={20} />
                {t.visitWebsite}
              </a>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <Target size={32} className="text-blue-600" />
                  <h3 className="text-2xl font-bold text-blue-600">{t.kastlerMission}</h3>
                </div>
                <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.kastlerMissionText}
                </p>
              </div>

              <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className="text-2xl font-bold mb-4 text-blue-600">{t.kastlerLevels}</h3>
                <div className="space-y-3">
                  {t.levels.map((level, i) => (
                    <div key={i} className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <h4 className="font-bold text-blue-600 mb-1">{level.name}</h4>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{level.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.campusPhotos}</h3>
              <div className="flex justify-center">
                <div className={`aspect-video w-full max-w-2xl rounded-xl overflow-hidden border-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <img 
                    src="images/abdo.png" 
                    alt="Campus Alfred Kastler - Casablanca" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'development':
        return (
          <div className="space-y-8">
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-4 mb-6">
                <Code size={40} className="text-blue-600" />
                <div>
                  <h2 className="text-3xl font-bold text-blue-600">{t.myRoleTitle}</h2>
                  <p className={`text-lg font-semibold mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.roleSubtitle}</p>
                </div>
              </div>
              <p className={`text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.roleIntro}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {t.responsibilities.map((responsibility, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{responsibility}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Website Development */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30' : 'bg-gradient-to-br from-blue-50 to-indigo-50'} border-2 border-blue-500`}>
              <div className="flex items-center gap-3 mb-4">
                <ExternalLink size={32} className="text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-600">{t.websiteTitle}</h3>
              </div>
              <p className={`text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.websiteDesc}</p>
              <div className="grid md:grid-cols-2 gap-3">
                {t.websiteFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Zap size={18} className="text-blue-500 flex-shrink-0 mt-1" />
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</p>
                  </div>
                ))}
              </div>
              <a 
                href="https://kastler.ma" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg mt-6"
              >
                <ExternalLink size={20} />
                Visiter kastler.ma
              </a>
            </div>

            {/* Tech Stack */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-3xl font-bold mb-8 text-blue-600">{t.techStackTitle}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h3 className="font-bold text-xl mb-4 text-blue-600">{t.frontend}</h3>
                  <div className="space-y-2">
                    {t.frontendTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-sm ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-green-600`}>
                  <h3 className="font-bold text-xl mb-4 text-green-600">{t.backend}</h3>
                  <div className="space-y-2">
                    {t.backendTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-sm ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-purple-600`}>
                  <h3 className="font-bold text-xl mb-4 text-purple-600">{t.database}</h3>
                  <div className="space-y-2">
                    {t.databaseTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-sm ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-yellow-600`}>
                  <h3 className="font-bold text-xl mb-4 text-yellow-600">{t.integrations}</h3>
                  <div className="space-y-2">
                    {t.integrationsTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-sm ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'architecture':
        return (
          <div className="space-y-8">
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-3xl font-bold mb-4 text-blue-600">{t.architectureTitle}</h2>
              <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.architectureDesc}
              </p>
            </div>

            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.layersTitle}</h3>
              <div className="space-y-4">
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Layers size={24} className="text-blue-600" />
                    <h4 className="font-bold text-xl text-blue-600">{t.presentationLayer}</h4>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.presentationLayerDesc}</p>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Settings size={24} className="text-blue-600" />
                    <h4 className="font-bold text-xl text-blue-600">{t.businessLayer}</h4>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.businessLayerDesc}</p>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Database size={24} className="text-blue-600" />
                    <h4 className="font-bold text-xl text-blue-600">{t.dataLayer}</h4>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.dataLayerDesc}</p>
                </div>
                <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Zap size={24} className="text-blue-600" />
                    <h4 className="font-bold text-xl text-blue-600">{t.realtimeLayer}</h4>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.realtimeLayerDesc}</p>
                </div>
              </div>
            </div>

            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-4 text-blue-600">{t.modulesTitle}</h3>
              <p className={`text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.modulesSubtitle}</p>
              <div className="space-y-4">
                {t.modules.map((module, index) => (
                  <div key={index} className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-blue-600`}>
                    <h4 className="font-bold text-xl mb-3 text-blue-600">{module.name}</h4>
                    <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {module.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-3 text-blue-500">{t.featuresTitle}</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.featuresSubtitle}</p>
            </div>

            {/* QR Badges Detailed Section */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
              <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
                <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 flex-shrink-0">
                  <QrCode size={32} color="white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-3 text-blue-500">Badges QR Code Intelligents - Recto/Verso</h3>
                  <p className={`text-base leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Système de cartes d'étudiant digitales avec QR code unique. Chaque badge présente un recto professionnel avec photo, nom et informations, et un verso avec code QR pour scan rapide. Génération automatique, impression haute qualité, et contrôle d'accès sécurisé.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <h4 className="font-bold text-blue-600 mb-2">Recto - Face Avant</h4>
                      <ul className="space-y-1 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Photo d'identité haute résolution</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Nom complet et prénom</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Niveau et filière</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Logo de l'école Alfred Kastler</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Design professionnel et sécurisé</span>
                        </li>
                      </ul>
                    </div>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <h4 className="font-bold text-blue-600 mb-2">Verso - Face Arrière</h4>
                      <ul className="space-y-1 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Code QR unique et crypté</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Informations de contact d'urgence</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Règles de conduite de l'école</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Numéro d'identification étudiant</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>Date de validité</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Prise de présence en 30 secondes</span>
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Sécurité renforcée</span>
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Cartes digitales ou imprimables</span>
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Génération automatique</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              {t.featuresList.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <div key={i} className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} hover:shadow-2xl transition-shadow`}>
                    <div className="flex flex-col md:flex-row items-start gap-6">
                      <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 flex-shrink-0">
                        <Icon size={32} color="white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-3 text-blue-500">{feature.title}</h3>
                        <p className={`text-base leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {feature.benefits.map((benefit, j) => (
                            <span key={j} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                              {benefit}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-3 text-blue-500">Espaces Utilisateurs Détaillés</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>4 rôles distincts avec interfaces personnalisées et fonctionnalités spécifiques</p>
            </div>

            <div className="grid gap-8">
              {userRoles.map((role, i) => {
                const Icon = role.icon;
                return (
                  <div key={i} className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex flex-col lg:flex-row items-start gap-6">
                      <div className={`p-4 rounded-xl bg-gradient-to-r ${role.color} flex-shrink-0`}>
                        <Icon size={32} color="white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-3 text-blue-500">{role.role}</h3>
                        <p className={`text-base leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {role.access}
                        </p>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-bold text-blue-600 mb-3">Fonctionnalités Principales</h4>
                            <ul className="space-y-2">
                              {role.features.map((feature, j) => (
                                <li key={j} className="flex items-start gap-2 text-sm">
                                  <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-bold text-blue-600 mb-3">Démonstration Vidéo</h4>
                            <div className={`aspect-video rounded-lg overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                              <video
                                ref={videoRefs[role.key]}
                                className="w-full h-full object-cover"
                                controls
                                onPlay={() => handleVideoPlay(role.key)}
                                onPause={() => handleVideoPause(role.key)}
                                poster={`/thumbnails/${role.key}.jpg`}
                              >
                                <source src={`/video/${role.videoFile}`} type="video/mp4" />
                                Votre navigateur ne supporte pas la lecture de vidéos.
                              </video>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Video size={16} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Interface {role.role} en action
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleVideoPlay(role.key)}
                                  className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${
                                    playingVideo === role.key
                                      ? 'bg-blue-600 text-white'
                                      : darkMode
                                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  <Play size={14} />
                                  {playingVideo === role.key ? 'En cours...' : 'Lecture'}
                                </button>
                                <button
                                  onClick={() => handleVideoPause(role.key)}
                                  className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${
                                    darkMode
                                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  <Pause size={14} />
                                  Pause
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <div className="flex items-start gap-3">
                <Video size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-blue-600 mb-2">Note sur les vidéos de démonstration</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Ces vidéos montrent l'interface réelle utilisée par chaque type d'utilisateur. 
                    Les vidéos sont des démonstrations enregistrées sur la plateforme en production, 
                    montrant le flux de travail complet pour chaque rôle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-3 text-blue-500">{t.galleryTitle}</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.gallerySubtitle}</p>
            </div>

            {/* Demo Video */}
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-4 text-blue-500">{t.demoVideoTitle}</h3>
              <p className={`text-base mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.demoVideoDesc}</p>
              
              <div className={`aspect-video w-full max-w-4xl mx-auto rounded-xl overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <video
                  ref={videoRefs.demo}
                  className="w-full h-full object-cover"
                  controls
                  onPlay={() => handleVideoPlay('demo')}
                  onPause={() => handleVideoPause('demo')}
                  poster="/thumbnails/demo.jpg"
                >
                  <source src="/video/demo.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>

              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => handleVideoPlay('demo')}
                  className={`px-6 py-2 rounded-lg font-semibold flex items-center gap-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                >
                  <Play size={20} />
                  {playingVideo === 'demo' ? 'En cours...' : 'Lancer la démonstration'}
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <h4 className="font-bold text-blue-600 mb-2">Contenu de la vidéo:</h4>
                  <ul className="space-y-1 text-sm">
                    <li>• Démonstration interface administrateur</li>
                    <li>• Génération et scan badges QR code</li>
                    <li>• Navigation portail parents</li>
                    <li>• Utilisation sur mobile et tablette</li>
                    <li>• Processus complet de gestion</li>
                  </ul>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                  <h4 className="font-bold text-blue-600 mb-2">Points clés démontrés:</h4>
                  <ul className="space-y-1 text-sm">
                    <li>• Responsive design sur tous appareils</li>
                    <li>• Workflow prise de présence QR code</li>
                    <li>• Notifications WhatsApp en temps réel</li>
                    <li>• Génération automatique de bulletins</li>
                    <li>• Interface intuitive pour tous les rôles</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Gallery Sections */}
            {galleryContent.map((section, sectionIndex) => (
              <div key={sectionIndex} className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className="text-2xl font-bold mb-6 text-blue-500">{section.category}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'} hover:scale-105 transition-transform cursor-pointer`}>
                      <div className="aspect-video flex items-center justify-center overflow-hidden bg-white">
                        {item.type === 'video' ? (
                          <div className="relative w-full h-full">
                            <video
                              className="w-full h-full object-cover"
                              src={`/video/${item.videoFile}`}
                              poster={`/thumbnails/${item.videoFile.replace('.mp4', '.jpg')}`}
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Play size={48} color="white" />
                            </div>
                          </div>
                        ) : item.image ? (
                          <img 
                            src={`/images/${item.image}`} 
                            alt={item.title}
                            className="w-full h-full object-contain p-2"
                          />
                        ) : (
                          <div className="text-center p-4 w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              {item.type === 'badge-front' ? (
                                <>
                                  <div className="w-32 h-40 mx-auto mb-2 border-2 border-gray-400 rounded-lg bg-white flex items-center justify-center">
                                    <div className="text-center">
                                      <div className="w-16 h-16 rounded-full bg-gray-300 mx-auto mb-2"></div>
                                      <div className="h-2 bg-gray-300 w-20 mx-auto mb-1"></div>
                                      <div className="h-2 bg-gray-300 w-16 mx-auto"></div>
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-600">Recto Badge</span>
                                </>
                              ) : item.type === 'badge-back' ? (
                                <>
                                  <div className="w-32 h-40 mx-auto mb-2 border-2 border-gray-400 rounded-lg bg-white flex items-center justify-center">
                                    <div className="w-20 h-20 border-2 border-dashed border-gray-400"></div>
                                  </div>
                                  <span className="text-xs text-gray-600">Verso QR Code</span>
                                </>
                              ) : item.type.includes('mobile') ? (
                                <>
                                  <div className="relative">
                                    <div className="w-24 h-40 border-4 border-gray-800 rounded-2xl bg-white mx-auto">
                                      <div className="h-6 bg-gray-800 rounded-t-lg"></div>
                                      <div className="p-2">
                                        <div className="h-2 bg-gray-300 w-16 mx-auto mb-1"></div>
                                        <div className="h-2 bg-gray-300 w-12 mx-auto mb-2"></div>
                                        <div className="space-y-1">
                                          <div className="h-2 bg-gray-200 w-full"></div>
                                          <div className="h-2 bg-gray-200 w-3/4"></div>
                                          <div className="h-2 bg-gray-200 w-1/2"></div>
                                        </div>
                                      </div>
                                    </div>
                                    <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                                      Mobile
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <ImageIcon size={48} className={darkMode ? 'text-gray-600' : 'text-gray-400'} />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-blue-500">{item.title}</h4>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {item.description}
                        </p>
                        {item.details && (
                          <div className="mt-3">
                            <ul className="space-y-1">
                              {item.details.slice(0, 3).map((detail, idx) => (
                                <li key={idx} className="text-xs flex items-start gap-1">
                                  <span className="text-blue-500">•</span>
                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                🎬 <strong>Note:</strong> Cette galerie montre l'étendue des captures d'écran et démonstrations disponibles. 
                En production réelle, chaque placeholder serait remplacé par des images/vidéos réelles de la plateforme.
              </p>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-8">
            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-3xl font-bold mb-8 text-center text-blue-600">{t.resultsTitle}</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.statsTitle}</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  {t.stats.map((stat, index) => (
                    <div key={index} className={`text-center p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-2 border-blue-600`}>
                      <div className="text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.challengesMetTitle}</h3>
                <div className="space-y-3">
                  {t.challengesMet.map((challenge, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Zap size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                      <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.concreteResultsTitle}</h3>
                <div className="space-y-3">
                  {t.concreteResults.map((result, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-1" />
                      <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-8 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                <h3 className="text-2xl font-bold mb-4 text-blue-600">{t.testimonialTitle}</h3>
                <blockquote className={`text-xl italic leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{t.testimonialQuote}"
                </blockquote>
                <p className={`font-bold text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  — {t.testimonialAuthor}
                </p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {t.testimonialSchool}
                </p>
              </div>
            </div>

            <div className={`p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-xl'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">{t.realUseTitle}</h3>
              <div className="space-y-4">
                {t.realUsePoints.map((point, i) => (
                  <div key={i} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex items-start gap-3`}>
                    <CheckCircle size={24} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-8`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-6 px-4 py-2 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100 shadow-md'
          } transition-colors`}
        >
          <ArrowLeft size={20} />
          <span>{t.backButton}</span>
        </button>

        {/* Hero Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className={`text-xl mb-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.subtitle}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : darkMode
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                    : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mb-12">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}